import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';

// Import Fontsource fonts to replicate legacy style
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

import '@fontsource/quicksand/300.css';
import '@fontsource/quicksand/400.css';
import '@fontsource/quicksand/500.css';
import '@fontsource/quicksand/700.css';

// Import print styles
import '../styles/print.css';

import {
  Box,
  Container,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link } from 'react-router';
import climadiagLogo from '../assets/images/logo_light_climadiag.png';
import { PrintProvider, usePrint } from '../providers/PrintProvider';
import PrintDialog from './PrintDialog';

/**
 * Reset document title
 * @param {string} title - Title to set
 */
const resetTitle = (title) => {
  document.title = title;
};

/**
 * PrintDialogWrapper - Component to handle print dialog state
 * Uses the new PrintProvider context
 * @returns {JSX.Element} PrintDialog wrapper component
 */
const PrintDialogWrapper = () => {
  const { isPrintDialogOpen, closePrintDialog } = usePrint();

  return (
    <PrintDialog open={isPrintDialogOpen} onClose={closePrintDialog} />
  );
};

/**
 * Main Layout component for the application
 * @param {Object} props - Component props
 * @param {string} props.title - Page title
 * @param {string} props.nom - Entity name
 * @param {boolean} props.hasHeader - Whether to show header
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} Layout component
 */
const Layout = ({
  title = 'Climadiag Commune / Météo France',
  nom,
  hasHeader = true,
  children,
  ...rest
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    // Handle print events
    const handleAfterPrint = () => resetTitle(title);

    globalThis.addEventListener('afterprint', handleAfterPrint);

    return () => {
      globalThis.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [title]);

  return (
    <div>
      <Helmet key={title}>
        <title>{title}</title>
      </Helmet>

      {hasHeader && (
        <Stack
          alignItems="center"
          justifyContent="space-between"
          direction="row"
          spacing={2}
          sx={{
            displayPrint: 'none',
            zIndex: 9,
            backgroundColor: 'white',
            position: 'fixed',
            top: 0,
            left: 0,
            paddingX: 2,
            height: 50,
            width: '100%',
            borderBottom: `1px solid ${theme.palette.secondary.main}`,
          }}
        >
          <Link to="/">
            <Box
              component="img"
              src={climadiagLogo}
              width={100}
              marginTop={1}
              alt="Climadiag Logo"
            />
          </Link>
          {isSmallScreen && nom && (
            <Typography
              textTransform="uppercase"
              sx={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                fontSize: '0.7rem',
                maxWidth: '200px',
              }}
            >
              {nom}
            </Typography>
          )}
        </Stack>
      )}

      <Container
        {...rest}
        sx={{
          pb: 2,
          minHeight: '100vh',
          ...rest.sx,
        }}
      >
        <PrintProvider>
          {children}
          <PrintDialogWrapper />
        </PrintProvider>
      </Container>
    </div>
  );
};

Layout.displayName = 'Layout';

export default Layout;
